/**
 * The **Column Center** — what monday's add-column gallery opens from its `More columns`
 * footer (`[c:add-column-more]`, spec M1-B10 §9 / q453).
 *
 * Measured on the sandbox, 1440 × 900:
 *
 *   modal            **1382 × 860** at (40, 40) — a 40 px inset on every side
 *   title            `Column Center` 24 px/300 at (68, 61)
 *   search           224 × 22 at (423, 65), placeholder *Search for a column type*
 *   close            28 × 32 at (1378, 56)
 *   left nav         290 × 366 at (56, 113), **40 px** rows — Featured · Essentials ·
 *                    Super useful · AI-powered · Team Power-Up · Board Power-Up · Combos ·
 *                    Marketplace apps
 *   section heading  24 px/500 at x 362
 *   cards            two-up (a 480 px pitch), each with an 18 px/600 title, a description and
 *                    a **`Add to board`** button (101 × 32)
 *
 * The five monday-only nav rows (AI-powered, the two Power-Ups, Combos, Marketplace apps) are
 * absent, per the §3.2 rule the rest of the shell follows; ours are the registry's own groups,
 * so a new column type appears here the moment it is registered and never needs a list edited
 * (CLAUDE.md rule 6).
 */
import { useEffect, useMemo, useState } from 'react';
import { Icon, icons } from '@elizmonday/ui';
import { galleryEntries } from '../../columns/registry';
import { useTableActions } from '../context';
import '../table.css';

/** monday's own first nav row: the eleven types its top-level gallery offers. */
const FEATURED_TYPES: readonly string[] = [
  'status', 'text', 'people', 'dropdown', 'date', 'numbers',
  'file', 'board_relation', 'timeline', 'checkbox', 'formula',
];

/** The registry's groups, with monday's vocabulary where the two coincide. */
const GROUPS: readonly { id: string; label: string }[] = [
  { id: 'essentials', label: 'Essentials' },
  { id: 'people', label: 'People' },
  { id: 'dates', label: 'Dates' },
  { id: 'numbers', label: 'Numbers' },
  { id: 'advanced', label: 'Advanced' },
];

export function ColumnCenter({
  onClose,
  initialQuery = '',
}: {
  onClose(): void;
  initialQuery?: string;
}) {
  const actions = useTableActions();
  const [query, setQuery] = useState(initialQuery);
  const [section, setSection] = useState('featured');
  const all = useMemo(() => galleryEntries(), []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const needle = query.trim().toLowerCase();
  const matching = useMemo(
    () =>
      needle === ''
        ? all
        : all.filter(
            (entry) =>
              entry.title.toLowerCase().includes(needle) ||
              entry.description.toLowerCase().includes(needle) ||
              entry.keywords.some((keyword) => keyword.includes(needle)),
          ),
    [all, needle],
  );

  /** A search spans every section — monday's does the same, and a scoped search hides answers. */
  const shown =
    needle !== ''
      ? matching
      : section === 'featured'
        ? FEATURED_TYPES.map((type) => matching.find((entry) => entry.type === type)).filter(
            (entry): entry is (typeof all)[number] => entry !== undefined,
          )
        : matching.filter((entry) => entry.group === section);

  const heading =
    needle !== ''
      ? `${matching.length} ${matching.length === 1 ? 'result' : 'results'}`
      : (GROUPS.find((group) => group.id === section)?.label ?? 'Featured');

  const add = (type: string, title: string) => {
    onClose();
    void actions.addColumn(type, title);
  };

  return (
    <div className="ez-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="ez-column-center"
        role="dialog"
        aria-modal="true"
        aria-label="Column Center"
        data-testid="column-center"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="ez-column-center__head">
          <h2 className="ez-column-center__title">Column Center</h2>
          <input
            className="ez-column-center__search"
            type="search"
            autoFocus
            aria-label="Search for a column type"
            placeholder="Search for a column type"
            data-testid="column-center-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <button
            type="button"
            className="ez-column-center__close"
            aria-label="Close"
            data-testid="column-center-close"
            onClick={onClose}
          >
            <Icon icon={icons.close} size={16} ignoreFocusStyle />
          </button>
        </header>

        <div className="ez-column-center__body">
          <ul className="ez-column-center__nav" role="tablist" aria-label="Column groups">
            {[{ id: 'featured', label: 'Featured' }, ...GROUPS].map((entry) => (
              <li key={entry.id}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={needle === '' && section === entry.id}
                  className="ez-column-center__nav-row"
                  data-testid={`column-center-nav-${entry.id}`}
                  onClick={() => {
                    setQuery('');
                    setSection(entry.id);
                  }}
                >
                  {entry.label}
                </button>
              </li>
            ))}
          </ul>

          <section className="ez-column-center__pane">
            <h3 className="ez-column-center__heading">{heading}</h3>
            {shown.length === 0 ? (
              <p className="ez-addcol__empty" data-testid="column-center-empty">
                No column type matches
              </p>
            ) : (
              <div className="ez-column-center__cards">
                {shown.map((entry) => (
                  <article className="ez-column-center__card" key={entry.type}>
                    <Icon icon={icons[entry.icon]} size={24} ignoreFocusStyle />
                    <div className="ez-column-center__card-text">
                      <h4>{entry.title}</h4>
                      <p>{entry.description}</p>
                    </div>
                    <button
                      type="button"
                      className="ez-column-center__add"
                      data-testid={`column-center-add-${entry.type}`}
                      onClick={() => add(entry.type, entry.title)}
                    >
                      Add to board
                    </button>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
